import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useWarranties } from "@/api/pos";

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("es-CO", { dateStyle: "medium" });
}

/**
 * Garantías de productos vendidos (cuarta y última pieza de "Inventario
 * avanzado"). No hay una gestión de garantías por separado — se busca
 * directamente sobre las líneas de venta que quedaron con una fecha de
 * vencimiento (ver `warranty.service.ts`), por cliente, producto o SKU.
 */
export default function WarrantiesPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "ACTIVA" | "VENCIDA">("");
  const { data: warranties, isLoading } = useWarranties({ search: search || undefined, status: status || undefined });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Garantías</h1>
        <p className="text-sm text-gray-500">Busque por cliente, producto o SKU para saber si una venta todavía tiene garantía vigente.</p>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          className="input-field flex-1 min-w-[240px]"
          placeholder="Buscar por cliente, documento, producto o SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as "" | "ACTIVA" | "VENCIDA")}>
          <option value="">Todas</option>
          <option value="ACTIVA">Vigentes</option>
          <option value="VENCIDA">Vencidas</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Venta</th>
              <th className="px-4 py-2.5">Producto</th>
              <th className="px-4 py-2.5">Cliente</th>
              <th className="px-4 py-2.5">Cantidad</th>
              <th className="px-4 py-2.5">Vence</th>
              <th className="px-4 py-2.5">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {warranties?.map((w) => (
              <tr key={w.saleLineId} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-700">
                  #{w.saleNumber} <span className="text-xs text-gray-400">({formatDate(w.saleDate.slice(0, 10))})</span>
                </td>
                <td className="px-4 py-2.5 text-gray-900">
                  {w.productName} <span className="text-xs text-gray-400">({w.productSku})</span>
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {w.customerName ?? "Cliente de mostrador"} {w.customerDocumentId && <span className="text-xs text-gray-400">({w.customerDocumentId})</span>}
                </td>
                <td className="px-4 py-2.5 text-gray-700">{w.quantity}</td>
                <td className="px-4 py-2.5 text-gray-700">{formatDate(w.warrantyEndDate)}</td>
                <td className="px-4 py-2.5">
                  <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${w.isExpired ? "bg-gray-100 text-gray-500" : "bg-green-100 text-green-700"}`}>
                    <ShieldCheck size={12} /> {w.isExpired ? "Vencida" : "Vigente"}
                  </span>
                </td>
              </tr>
            ))}
            {warranties?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Sin resultados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
